/**
 * Page-level FAQ data — keyed by route. Each list is unique per page
 * (no duplicated content across pages). The Q&As are used to inject
 * FAQPage JSON-LD into the <head> of each route, which is invisible to
 * users but boosts SEO text content and is eligible for Google FAQ
 * rich results.
 */

export const FAQ_HOME = [
    {
        q: "What is independent strata roof asset management?",
        a: "Independent strata roof asset management is structured technical advice on the condition, compliance and capital planning of a roof, delivered by a firm whose only income is professional advice, so every recommendation is guided by what the building genuinely needs. SRM operates exclusively under this model across Australia.",
    },
    {
        q: "How is SRM different from a roofing contractor?",
        a: "A roofing contractor earns revenue when repairs are scoped and invoiced — so their inspection is, by structure, a quoting exercise. SRM is a consultancy: its only deliverable is professional technical advice, and its only income comes from that advice — which is why every recommendation is shaped purely by the building's condition, giving committees genuinely impartial counsel.",
    },
    {
        q: "Who is SRM for?",
        a: "SRM partners with strata committees, body corporates, building managers, strata managers, asset owners and institutional property clients across every Australian state and territory. Engagements range from one-off independent assessments through to long-term roof guardianship and capital-works planning.",
    },
    {
        q: "What does SRM actually deliver?",
        a: "Six core services across the full roof asset lifecycle: roof condition assessments, storm and insurance investigations, compliance reporting, lifecycle and capital works planning, project oversight on major works, and an ongoing roof guardianship program. Every deliverable is committee-ready and built to withstand insurance and compliance scrutiny.",
    },
    {
        q: "How quickly can SRM attend a site?",
        a: "Initial enquiries are responded to within one business day. Site visits are usually scheduled within one to two weeks depending on location and urgency. Storm-response assessments and insurance investigations are prioritised and can often be attended sooner where access is available.",
    },
];

export const FAQ_SERVICES = [
    {
        q: "What does a roof condition assessment include?",
        a: "Comprehensive visual inspection (with drone imagery where access is restricted), documentation of every observed defect, photographed evidence, an annotated roof plan, structured zoning of roof condition, and a defensible written report with prioritised recommendations and an indicative service life by zone.",
    },
    {
        q: "What is the Guardian Program\u2122?",
        a: "The Guardian Program\u2122 is SRM's flagship engagement — a continuous, independent stewardship of the roof asset over multiple years. It combines the Guardian Assessment\u2122 baseline with Guardian Stewardship\u2122 (annual re-assessments, storm response, works oversight) and the Guardian History\u2122 long-term record. One program, one point of contact, across the full life of the roof.",
    },
    {
        q: "Can SRM support insurance claims?",
        a: "Yes. SRM produces independent technical reports that document storm or impact damage with photographic evidence, condition reasoning and a defensible scope. These reports are routinely accepted by insurers, loss adjusters and tribunals because they are independent of any repair revenue.",
    },
    {
        q: "Does SRM project-manage roof works?",
        a: "SRM provides owner-side project oversight on major works — reviewing contractor scope, witnessing critical work stages, verifying compliance with the inspection-led specification and producing a defensible record of execution. SRM does not act as the head contractor or take commission on the works.",
    },
    {
        q: "What does SRM cost?",
        a: "Engagements are consultation-based — a fixed fee for the agreed scope. Pricing is shared transparently with the committee before any work begins and is structured to deliver evidence that supports far larger capital and insurance decisions downstream. Request an assessment for a fee quote tailored to the building.",
    },
];

export const FAQ_ABOUT = [
    {
        q: "What is the purpose of SRM?",
        a: "SRM exists to give Australian strata communities, body corporates and asset owners structurally independent advice on their roof assets — separating the act of inspecting and recommending from the act of quoting and earning. Every report is written for the committee and the building, not for the contractor that follows.",
    },
    {
        q: "Where does SRM operate?",
        a: "SRM is headquartered on the Sunshine Coast, Queensland and serves clients Australia-wide. Field assessments are routinely conducted across QLD, NSW, VIC, ACT, WA, SA, TAS and the NT, with local logistics partners as required.",
    },
    {
        q: "Why does independence matter on a roof?",
        a: "A roof is the highest-cost, highest-risk asset in most strata buildings. When the inspector earns commission on the repairs, every recommendation carries an upward bias toward more work, broader scope and higher invoices. Independence removes that bias — and gives the committee a defensible record of why each decision was made.",
    },
    {
        q: "Who founded SRM?",
        a: "SRM was founded by Andy Nagy, drawing on more than twenty years of construction, roofing and building consultancy experience across the Australian residential, commercial and strata sectors. The firm has grown into a national consultancy supported by a team of trade-qualified inspectors and technical writers.",
    },
];

export const FAQ_TEAM = [
    {
        q: "Who leads SRM?",
        a: "SRM is led by founder Andy Nagy (General Manager) and Amanda Belot (Business Development Manager). Together they bring decades of combined experience in roofing, construction, building consultancy and strata community engagement across Australia.",
    },
    {
        q: "Are SRM inspectors trade-qualified?",
        a: "Yes. Every SRM inspector is trade-qualified in roofing or construction, which gives the field assessment a builder-level diagnostic rigour that desk-based or generalist consultancies cannot match. Inspections are paired with engineer-grade technical reasoning before any recommendation is written.",
    },
    {
        q: "How do I contact the SRM team directly?",
        a: "Amanda Belot (Business Development) can be reached on 0407 053 267. Andy Nagy (General Manager) can be reached on 0497 372 914. All written enquiries are routed to info@strataroofmanagement.com.au and acknowledged within one business day.",
    },
];

export const FAQ_RESOURCES = [
    {
        q: "What is the SRM Knowledge Centre?",
        a: "A curated library of plain-English technical articles, guides and explainers for strata committees, body corporates and asset owners. Topics include condition reporting, insurance evidence, compliance obligations, capital works planning and how to evaluate roofing contractor quotes.",
    },
    {
        q: "Who writes the SRM Resources articles?",
        a: "Articles are written by SRM's senior technical team — trade-qualified inspectors and consultancy leads — and reviewed for independence before publication. They are not sponsored, do not promote any contractor, and do not contain commission-linked product recommendations.",
    },
    {
        q: "Can SRM provide custom guides for our committee?",
        a: "Yes. SRM regularly produces committee-specific briefing notes, lifecycle summaries and capital works inputs as part of engagement deliverables. Request these alongside your assessment booking.",
    },
];

export const FAQ_CONTACT = [
    {
        q: "How do I book a roof assessment with SRM?",
        a: "Use the enquiry form on the contact page, email info@strataroofmanagement.com.au, or call Amanda Belot on 0407 053 267 or Andy Nagy on 0497 372 914. Provide the building address and a brief outline of the issue or the reason for the engagement — initial response is within one business day.",
    },
    {
        q: "Where is SRM based?",
        a: "SRM is headquartered on the Sunshine Coast, Queensland, and provides assessments, reporting and oversight across all Australian states and territories. There is no minimum engagement size for strata or body-corporate buildings.",
    },
    {
        q: "What information should I include in an enquiry?",
        a: "Property address, building type (strata, body corporate, commercial, government), brief reason for the enquiry (condition, insurance, capital planning, compliance), and your preferred contact channel. The more context provided, the faster a tailored scope and fee can be returned.",
    },
    {
        q: "Where do my enquiry submissions go?",
        a: "All submissions are received by SRM and reviewed by our team. Details are kept confidential.",
    },
];
